import {
  DataFilterDefinitionsDtoResponse,
  NavigationCustomizationItemDto,
} from '@asset-metrix/portal-gateway-client-js';

import {
  FilterConfigDto,
  FilterData,
  isDateRangeFilter,
  isMultiSelectFilter,
  isNumberRangeFilter,
  isSearchStringFilter,
  isSingleSelectFilter,
} from '../../store/states/filterTypes';
import { PermanentError } from '../../utils/errors';
import { getDetailsFromSearchParams } from '../../utils/getDetailsFromSearchParams';

export function validateSingleFilter<
  T extends keyof DataFilterDefinitionsDtoResponse['data'],
>(
  filterConfig: FilterConfigDto,
  filterData: DataFilterDefinitionsDtoResponse['data'][T],
  activeDynamicNavigationItem?: NavigationCustomizationItemDto
): FilterData {
  if (filterData == null) {
    throw new PermanentError(
      `Did not receive page filter data for filter key ${filterConfig.key}.`
    );
  }

  if (filterData.type === 'error') {
    throw new PermanentError(
      `An error occurred while retrieving filter data for filter key ${filterConfig.key}.`,
      filterData.cause
    );
  }

  // check if active dynamic nav item has a valid details value
  if (activeDynamicNavigationItem?.href) {
    const dynamicItemUrlSearch = new URLSearchParams(
      activeDynamicNavigationItem?.href.split('?').pop()
    );
    const { detailsDiscriminator, detailsValue } =
      getDetailsFromSearchParams(dynamicItemUrlSearch);

    if (
      detailsDiscriminator === filterConfig.key &&
      filterData.selection == null
    ) {
      throw new Error(
        `An error occurred while retrieving data for details page filter key ${filterConfig.key}. \
        The filter value '${detailsValue}' is not a valid selection.`
      );
    }
  }

  // Matching filter configs with filter data is quite hard right now as we're missing useful discriminators
  if (
    filterConfig.display === 'multi-select' &&
    isMultiSelectFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else if (
    filterConfig.display === 'single-select' &&
    isSingleSelectFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else if (
    filterConfig.display === 'single-date-picker' &&
    filterData.type === 'date' &&
    isSingleSelectFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else if (
    filterConfig.display === 'range-date-picker' &&
    isDateRangeFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else if (
    filterConfig.display === 'range-number-picker' &&
    isNumberRangeFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else if (
    filterConfig.display === 'search' &&
    isSearchStringFilter(filterData)
  ) {
    return {
      display: filterConfig.display,
      ...filterData,
    };
  } else {
    throw new PermanentError(
      `Mapping of filter config to filter date failed for key ${filterConfig.key}`
    );
  }
}

export function validateFilters(
  config: { filters?: FilterConfigDto[] },
  data: DataFilterDefinitionsDtoResponse,
  activeDynamicNavigationItem?: NavigationCustomizationItemDto
) {
  // validate all filters
  return (config.filters ?? []).reduce<Record<string, FilterData>>(
    (acc, filterConfig) => {
      const filterData = data.data[filterConfig.key];
      acc[filterConfig.key] = validateSingleFilter(
        filterConfig,
        filterData,
        activeDynamicNavigationItem
      );
      return acc;
    },
    {}
  );
}
